import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Languages, Mail, Lock, User, Wallet, Eye, EyeOff, Loader2, UserCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, walletLogin, guestLogin, processGoogleAuth } = useAuth();
  const { t, language } = useLanguage();
  const googleAuthProcessing = useRef(false);
  
  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  
  // Process Google OAuth callback (session_id in URL hash)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=') && !googleAuthProcessing.current) {
      googleAuthProcessing.current = true;
      setGoogleLoading(true);
      
      // Extract session_id from hash
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        processGoogleAuth(sessionId).then((result) => {
          setGoogleLoading(false);
          if (result?.success) {
            navigate("/language-selection");
          } else {
            setError(result?.error || "Erro ao fazer login com Google.");
          }
        });
      } else {
        setGoogleLoading(false);
      }
    }
  }, [processGoogleAuth, navigate]);
  
  // Handle Google login button click
  // Handle Google login button click
  const handleGoogleLogin = () => {
    setError("");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth';
    const appName = encodeURIComponent('LCF Translator');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&name=${appName}`;
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, lang: language }),
      });
      if (res.ok) setForgotSent(true);
      else setError(t("authGenericError"));
    } catch {
      setError(t("authGenericError"));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(email, password, name);
      }
      
      if (result.success) {
        navigate("/dashboard");
      } else {
        setError(result.error);
        // If email already registered, switch to login mode
        if (result.error && result.error.includes("já está cadastrado")) {
          setIsLogin(true);
        }
      }
    } catch (err) {
      setError("Ocorreu um erro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleWalletConnect = async (walletType) => {
    setError("");
    setLoading(true);
    
    try {
      let provider;
      
      if (walletType === "phantom") {
        if (!window.solana?.isPhantom) {
          window.open("https://phantom.app/", "_blank");
          setError("Por favor, instale a carteira Phantom.");
          setLoading(false);
          return;
        }
        provider = window.solana;
      } else if (walletType === "solflare") {
        if (!window.solflare?.isSolflare) {
          window.open("https://solflare.com/", "_blank");
          setError("Por favor, instale a carteira Solflare.");
          setLoading(false);
          return;
        }
        provider = window.solflare;
      }
      
      // Connect to wallet
      const response = await provider.connect();
      const publicKey = response.publicKey.toString();
      
      // Create message to sign
      const message = `Login to Real-time Translator\nTimestamp: ${Date.now()}`;
      const encodedMessage = new TextEncoder().encode(message);
      
      // Sign message
      const signedMessage = await provider.signMessage(encodedMessage, "utf8");
      
      // Convert signature to base64 without using Buffer (browser compatible)
      const signatureBytes = signedMessage.signature;
      const signatureBase64 = btoa(String.fromCharCode(...new Uint8Array(signatureBytes)));
      
      // Login with wallet
      const result = await walletLogin(publicKey, signatureBase64, message);
      
      if (result.success) {
        navigate("/dashboard");
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error("Wallet connection error:", err);
      if (err.code === 4001) {
        setError("Conexão cancelada pelo usuário.");
      } else if (err.message?.includes("User rejected")) {
        setError("Conexão cancelada pelo usuário.");
      } else if (!window.solana && !window.solflare) {
        setError("Nenhuma carteira Solana detectada. Instale Phantom ou Solflare.");
      } else {
        setError("Erro ao conectar carteira. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleGuestAccess = async () => {
    setError("");
    setGuestLoading(true);
    
    try {
      const result = await guestLogin();
      
      if (result.success) {
        navigate("/");
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Ocorreu um erro. Tente novamente.");
    } finally {
      setGuestLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <Languages className="w-10 h-10 text-white" />
        <h1 className="text-2xl font-bold text-white font-['Outfit']">
          {t("appTitle")}
        </h1>
      </div>
      
      <Card className="w-full max-w-md bg-zinc-900/50 border-zinc-800">
        <CardHeader className="text-center">
          <CardTitle className="text-white text-xl">
            {forgotMode ? t("authForgotTitle") : isLogin ? t("authSignIn") : t("authSignUp")}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {forgotMode
              ? t("authForgotDesc")
              : isLogin 
              ? t("authSignInSubtitle") 
              : t("authSignUpSubtitle")}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-800">
              <TabsTrigger value="email" className="data-[state=active]:bg-zinc-700">
                <Mail className="w-4 h-4 mr-2" />
                {t("authEmail")}
              </TabsTrigger>
              <TabsTrigger value="wallet" className="data-[state=active]:bg-zinc-700">
                <Wallet className="w-4 h-4 mr-2" />
                {t("authWallet")}
              </TabsTrigger>
            </TabsList>
            
            {/* Email Login/Register */}
            <TabsContent value="email" className="mt-4">
              {forgotMode ? (
                <div className="space-y-4" data-testid="forgot-password-panel">
                  {forgotSent ? (
                    <div className="text-center space-y-3" data-testid="forgot-password-sent">
                      <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto" />
                      <p className="text-zinc-200 text-sm">{t("authResetEmailSent")}</p>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email" className="text-zinc-300">{t("authEmail")}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                          <Input
                            id="forgot-email"
                            data-testid="forgot-password-email-input"
                            type="email"
                            placeholder={t("authEmailPlaceholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>
                      </div>
                      {error && <p className="text-red-400 text-sm" data-testid="forgot-password-error">{error}</p>}
                      <Button type="submit" disabled={loading} data-testid="forgot-password-submit" className="w-full bg-white text-black hover:bg-zinc-200">
                        {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {t("authSendResetLink")}
                      </Button>
                    </form>
                  )}
                  <div className="text-center">
                    <button
                      type="button"
                      data-testid="forgot-password-back"
                      onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }}
                      className="text-zinc-400 hover:text-white text-sm"
                    >
                      {t("authBackToLogin")}
                    </button>
                  </div>
                </div>
              ) : (
              <>
              <form onSubmit={handleEmailAuth} className="space-y-4">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-300">{t("authName")}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="name"
                        type="text"
                        placeholder={t("authNamePlaceholder")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">{t("authEmail")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t("authEmailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-zinc-300">{t("authPassword")}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {isLogin && (
                    <div className="text-right">
                      <button
                        type="button"
                        data-testid="forgot-password-link"
                        onClick={() => { setForgotMode(true); setError(""); }}
                        className="text-xs text-zinc-400 hover:text-white underline-offset-2 hover:underline"
                      >
                        {t("authForgotPassword")}
                      </button>
                    </div>
                  )}
                </div>
                
                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}
                
                <Button
                  type="submit"
                  disabled={loading || googleLoading}
                  className="w-full bg-white text-black hover:bg-zinc-200"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {isLogin ? t("authSignIn") : t("authSignUp")}
                </Button>
              </form>
              
              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-700"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-zinc-900 px-2 text-zinc-500">{t("authOrContinueWith")}</span>
                </div>
              </div>
              
              {/* Google Login Button */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading || googleLoading}
                variant="outline"
                className="w-full border-zinc-700 hover:bg-zinc-800 text-white h-11"
              >
                {googleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <GoogleIcon />
                )}
                <span className="ml-2">{t("authContinueGoogle")}</span>
              </Button>
              
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError("");
                  }}
                  className="text-zinc-400 hover:text-white text-sm"
                >
                  {isLogin 
                    ? `${t("authNoAccount")} ${t("authSignUp")}` 
                    : `${t("authHasAccount")} ${t("authSignIn")}`}
                </button>
              </div>
              
              {/* Guest Access */}
              <div className="mt-6 pt-4 border-t border-zinc-800">
                <Button
                  type="button"
                  onClick={handleGuestAccess}
                  disabled={guestLoading || loading}
                  variant="ghost"
                  className="w-full text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 h-auto py-3"
                >
                  {guestLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2 flex-shrink-0" />
                  ) : (
                    <UserCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  )}
                  <span className="text-xs sm:text-sm text-center whitespace-normal leading-tight">
                    {t("authAccessAsGuest")}
                  </span>
                </Button>
              </div>
              </>
              )}
            </TabsContent>
            
            {/* Wallet Login */}
            <TabsContent value="wallet" className="mt-4 space-y-4">
              <p className="text-zinc-400 text-sm text-center mb-4">
                {t("authConnectWalletDesc")}
              </p>
              
              <Button
                onClick={() => handleWalletConnect("phantom")}
                disabled={loading}
                variant="outline"
                className="w-full border-purple-600 hover:bg-purple-600/20 text-purple-400 hover:text-purple-300 h-12"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Wallet className="w-5 h-5 mr-2" />
                )}
                Connect with Phantom
              </Button>
              
              <Button
                onClick={() => handleWalletConnect("solflare")}
                disabled={loading}
                variant="outline"
                className="w-full border-orange-600 hover:bg-orange-600/20 text-orange-400 hover:text-orange-300 h-12"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Wallet className="w-5 h-5 mr-2" />
                )}
                Connect with Solflare
              </Button>
              
              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}
              
              {/* Guest Access */}
              <div className="mt-6 pt-4 border-t border-zinc-800">
                <Button
                  type="button"
                  onClick={handleGuestAccess}
                  disabled={guestLoading || loading}
                  variant="ghost"
                  className="w-full text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 h-auto py-3"
                >
                  {guestLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2 flex-shrink-0" />
                  ) : (
                    <UserCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  )}
                  <span className="text-xs sm:text-sm text-center whitespace-normal leading-tight">
                    {t("authAccessAsGuest")}
                  </span>
                </Button>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="text-center space-y-2">
            <Link to="/" className="text-zinc-500 hover:text-zinc-300 text-sm block">
              ← {t("authBackToHome")}
            </Link>
            <Link to="/diagnostics" className="text-purple-500 hover:text-purple-400 text-xs block">
              {t("authDiagnostics")}
            </Link>
          </div>
        </CardContent>
      </Card>
      
      {/* Copyright */}
      <p className="mt-8 text-zinc-700 text-[10px] sm:text-xs text-center">
        Developed by João Vitor Fernandes Farias. All rights reserved.
      </p>
    </div>
  );
}
